import { BookOpen, Check, Gift, LockKeyhole, PartyPopper, Star, Trophy } from "lucide-react";
import { formatRoom, formatGrade } from "@/lib/classroom";
import { formatNumber, percentOf, pluralize } from "@/lib/format";
import { rewardSummary } from "@/lib/prizes";
import { amountToGo, nextReward, tvMoney } from "@/lib/tvDisplay";
import FitText from "@/components/tv/FitText";

/**
 * The TV display's slides, each laid out for the fixed 1760×816 body of
 * the 1920×1080 stage (components/tv/TvStage). Sizes are in px for that
 * reason: the stage scales as a whole to whatever screen it's on. Names
 * and prizes go through FitText, which shrinks and then wraps them
 * within a fixed box rather than cutting them off.
 */

export function GoalSlide({ campaign, rewards }) {
  const moneyPct = campaign.money_goal_cents ? percentOf(campaign.raised_cents, campaign.money_goal_cents, { floor: true }) : null;
  const minutePct = campaign.minute_goal ? percentOf(campaign.minutes_logged, campaign.minute_goal, { floor: true }) : null;
  const next = nextReward(rewards);
  const allUnlocked = rewards.length > 0 && !next;

  return (
    <div className="flex h-full flex-col gap-8">
      <div className="grid min-h-0 flex-1 grid-cols-[1.25fr_1fr] gap-8">
        <BigStat
          label="Raised so far"
          value={tvMoney(campaign.raised_cents)}
          pct={moneyPct}
          goal={campaign.money_goal_cents ? `of our ${tvMoney(campaign.money_goal_cents)} goal` : null}
          detail={`from ${pluralize(campaign.donor_count, "donor")}`}
          barClass="bg-primary"
          large
        />
        <BigStat
          label="Minutes read"
          value={formatNumber(campaign.minutes_logged)}
          pct={minutePct}
          goal={campaign.minute_goal ? `of our ${formatNumber(campaign.minute_goal)} minute goal` : null}
          detail={hoursLine(campaign.minutes_logged)}
          barClass="bg-accent"
        />
      </div>

      {next && (
        <div className="flex items-center gap-7 rounded-2xl border border-primary/30 bg-primary/5 px-10 py-7">
          <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Gift className="h-10 w-10" strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[26px] font-semibold uppercase tracking-wider text-primary">Next school reward</p>
            <FitText max={52} min={32} height={92} className="font-display font-semibold text-foreground">{next.prize}</FitText>
          </div>
          <div className="shrink-0 text-right">
            <p className="font-display text-[56px] font-semibold leading-none text-foreground font-tabular">{amountToGo(next)}</p>
            <p className="mt-2 text-[26px] text-muted-foreground">to go · {rewardSummary(next).toLowerCase()}</p>
          </div>
        </div>
      )}
      {allUnlocked && (
        <div className="flex items-center gap-7 rounded-2xl border border-success/40 bg-success/10 px-10 py-7">
          <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
            <PartyPopper className="h-10 w-10" strokeWidth={2} />
          </span>
          <p className="font-display text-[52px] font-semibold leading-tight text-foreground">
            Every school reward unlocked!
          </p>
        </div>
      )}
    </div>
  );
}

function BigStat({ label, value, pct, goal, detail, barClass, large = false }) {
  return (
    <section className="flex min-h-0 flex-col rounded-3xl border border-border bg-card px-12 py-12 shadow-sm">
      <p className="text-[28px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      {/* One line, shrinking for a big school's 1,000,000+ minutes or $1M+. */}
      <FitText max={large ? 176 : 136} min={72} lineHeight={1} className="mt-3 font-display font-semibold text-foreground font-tabular">
        {value}
      </FitText>
      {pct !== null ? (
        <div className="mt-10">
          <div className="flex items-baseline justify-between gap-6">
            <p className="text-[32px] text-muted-foreground">{goal}</p>
            <p className="font-display text-[48px] font-semibold leading-none text-foreground font-tabular">{pct}%</p>
          </div>
          <Meter pct={pct} barClass={barClass} className="mt-4 h-10" />
          <p className="mt-4 text-[28px] text-muted-foreground">{detail}</p>
        </div>
      ) : (
        <p className="mt-6 text-[32px] text-muted-foreground">{detail}</p>
      )}
    </section>
  );
}

function hoursLine(minutes) {
  const hours = Math.floor((minutes || 0) / 60);
  return hours >= 1 ? `That's ${pluralize(hours, "hour")} of reading` : "Every minute counts";
}

export function RewardsSlide({ slide, rewards }) {
  const unlocked = rewards.filter((r) => r.unlocked).length;

  return (
    <div className="flex h-full flex-col">
      <SlideTitle
        title="School rewards"
        subtitle="We unlock these together, as a whole school"
        aside={`${unlocked} of ${rewards.length} unlocked`}
        slide={slide}
      />
      <ol className="mt-8 flex min-h-0 flex-1 flex-col gap-4">
        {slide.items.map((reward) => (
          <RewardRow key={reward.id} reward={reward} />
        ))}
      </ol>
    </div>
  );
}

function RewardRow({ reward }) {
  const barClass = reward.unlocked ? "bg-success" : reward.kind === "money" ? "bg-primary" : "bg-accent";

  return (
    <li
      className={`grid max-h-[148px] min-h-0 flex-1 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-8 rounded-2xl border px-9 ${
        reward.unlocked ? "border-success/40 bg-success/10" : "border-border bg-card"
      }`}
    >
      <span
        className={`flex h-[72px] w-[72px] items-center justify-center rounded-full ${
          reward.unlocked ? "bg-success text-success-foreground" : "bg-secondary text-muted-foreground"
        }`}
      >
        {reward.unlocked ? <Check className="h-10 w-10" strokeWidth={3} /> : <LockKeyhole className="h-9 w-9" strokeWidth={2.25} />}
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline gap-6">
          <FitText max={44} min={28} height={69} className="flex-1 font-display font-semibold text-foreground">{reward.prize}</FitText>
          <p className="shrink-0 text-[26px] text-muted-foreground">{rewardSummary(reward)}</p>
        </div>
        <Meter pct={reward.progress_pct} barClass={barClass} className="mt-3 h-4" />
      </div>
      <div className="w-[220px] text-right">
        {reward.unlocked ? (
          <p className="font-display text-[44px] font-semibold text-success">Unlocked!</p>
        ) : (
          <>
            <p className="font-display text-[48px] font-semibold leading-none text-foreground font-tabular">{reward.progress_pct}%</p>
            <p className="mt-2 text-[24px] text-muted-foreground font-tabular">{amountToGo(reward)} to go</p>
          </>
        )}
      </div>
    </li>
  );
}

export function TopReadersSlide({ readers }) {
  const featured = readers.slice(0, 3);
  const rest = readers.slice(3);

  return (
    <div className="flex h-full flex-col">
      <SlideTitle title="Top readers" subtitle="Most minutes read across the school" icon={Trophy} />
      <div className={`mt-8 grid min-h-0 flex-1 gap-8 ${rest.length ? "grid-cols-[1.1fr_1fr]" : "grid-cols-1"}`}>
        <ol className="flex min-h-0 flex-col gap-5">
          {/* Keyed by position: public names can repeat ("Liam S." twice). */}
          {featured.map((reader, i) => (
            <li key={i} className="flex max-h-[200px] min-h-0 flex-1 items-center gap-8 rounded-3xl border border-primary/25 bg-card px-10 shadow-sm">
              <RankBadge rank={reader.rank} size="lg" />
              <div className="min-w-0 flex-1">
                <FitText max={60} min={36} height={88} className="font-display font-semibold text-foreground">{reader.name}</FitText>
                <FitText max={28} min={20} height={58} lineHeight={1.3} className="text-muted-foreground">{formatRoom(reader.classroom_name)}</FitText>
              </div>
              <Minutes value={reader.minutes} className="text-[52px]" />
            </li>
          ))}
        </ol>
        {rest.length > 0 && (
          <ol className="flex min-h-0 flex-col gap-3">
            {rest.map((reader, i) => (
              <li key={i} className="flex max-h-[104px] min-h-0 flex-1 items-center gap-6 rounded-2xl border border-border bg-card px-7">
                <RankBadge rank={reader.rank} />
                <div className="min-w-0 flex-1">
                  <FitText max={38} min={26} className="font-display font-semibold text-foreground">{reader.name}</FitText>
                  <FitText max={24} min={18} lineHeight={1.2} className="text-muted-foreground">{formatRoom(reader.classroom_name)}</FitText>
                </div>
                <Minutes value={reader.minutes} className="text-[36px]" />
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

export function ClassesSlide({ slide }) {
  // Up to 6 cards, three across, so long room and student names have
  // room to shrink and wrap.
  const columns = Math.min(slide.items.length, 3);
  const rows = Math.ceil(slide.items.length / columns);

  return (
    <div className="flex h-full flex-col">
      <SlideTitle title="Top reader in every class" subtitle="Most minutes read in each classroom" slide={slide} />
      <ul
        className="mt-8 grid min-h-0 flex-1 gap-6"
        style={{
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 300px))`,
        }}
      >
        {slide.items.map((classroom) => (
          <ClassCard key={classroom.id} classroom={classroom} />
        ))}
      </ul>
    </div>
  );
}

function ClassCard({ classroom }) {
  const hasLeader = classroom.leader_count > 0;

  return (
    <li className="flex min-h-0 flex-col justify-between rounded-2xl border border-border bg-card px-10 py-7">
      <div>
        <FitText max={48} min={28} height={66} className="font-display font-semibold text-foreground">{formatRoom(classroom.name)}</FitText>
        <p className="mt-1 flex items-center gap-2 text-[26px] text-muted-foreground font-tabular">
          <BookOpen className="h-6 w-6 shrink-0" />
          {classroom.grade && `${formatGrade(classroom.grade)} · `}
          {formatNumber(classroom.minutes_logged)} min as a class
        </p>
      </div>
      <div className="mt-4 border-t border-border pt-4">
        {hasLeader ? (
          <div className="flex items-center gap-3">
            <Star className="h-8 w-8 shrink-0 fill-primary text-primary" />
            <FitText max={42} min={26} height={60} fallback={classroom.leader_count > 1 ? `${classroom.leader_count} readers tied` : undefined} className="flex-1 font-display font-semibold text-foreground">
              {leaderLine(classroom)}
            </FitText>
            <p className="shrink-0 text-[32px] font-semibold text-primary font-tabular">{formatNumber(classroom.leader_minutes)} min</p>
          </div>
        ) : (
          <p className="text-[28px] leading-tight text-muted-foreground">No minutes yet</p>
        )}
      </div>
    </li>
  );
}

// "Ava R.", "Ava R. & Leo M.", "Ava R., Leo M. & Sam K.", "4 readers tied"
function leaderLine({ leader_names: names, leader_count: count }) {
  if (count > names.length) return `${count} readers tied`;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
}

function SlideTitle({ title, subtitle, aside, slide, icon: Icon }) {
  const paged = slide?.pages > 1;
  return (
    <div className="flex items-end justify-between gap-8">
      <div className="min-w-0">
        <h2 className="flex items-center gap-4 font-display text-[68px] font-semibold leading-none text-foreground">
          {Icon && <Icon className="h-14 w-14 text-primary" strokeWidth={2} />}
          {title}
        </h2>
        <p className="mt-3 text-[30px] text-muted-foreground">{subtitle}</p>
      </div>
      {(aside || paged) && (
        <p className="shrink-0 text-right text-[30px] font-medium text-muted-foreground font-tabular">
          {aside}
          {aside && paged && " · "}
          {paged && `${slide.page} of ${slide.pages}`}
        </p>
      )}
    </div>
  );
}

// A rank in Fraunces; the top three in clay (DESIGN.md, leaderboards).
function RankBadge({ rank, size = "md" }) {
  const box = size === "lg" ? "h-[104px] w-[104px] text-[56px]" : "h-16 w-16 text-[34px]";
  const tone = rank <= 3 ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground";
  return (
    <span className={`flex shrink-0 items-center justify-center rounded-full font-display font-semibold font-tabular ${box} ${tone}`}>
      {rank}
    </span>
  );
}

function Minutes({ value, className }) {
  return (
    <p className={`shrink-0 font-display font-semibold text-foreground font-tabular ${className}`}>
      {formatNumber(value)}
      <span className="ml-2 font-sans text-[0.55em] font-medium text-muted-foreground">min</span>
    </p>
  );
}

function Meter({ pct, barClass, className }) {
  const width = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className={`w-full overflow-hidden rounded-full border border-border bg-secondary ${className}`}>
      <div className={`h-full rounded-full ${barClass} transition-[width] duration-1000 motion-reduce:transition-none`} style={{ width: `${width}%` }} />
    </div>
  );
}
